import { useState } from 'react'

import { GAMES, money, TRIES_PER_QUESTION, type SkillLevels, type TrolleyView } from '@hazel/shared'

import { Card } from '../components/Card.js'
import { KeyLegend } from '../components/KeyLegend.js'
import { LEAVE_SHORTCUT } from '../components/shortcuts.js'
import { useBarTitle } from '../layouts/bar-title.js'
import { Announce } from './Announce.js'
import { LeaveGame } from './LeaveGame.js'
import { Milestone } from './Milestone.js'
import { PlayAgain } from './PlayAgain.js'
import { NumberPad, MAX_DIGITS } from './NumberPad.js'
import { questionClassName } from './trolleyLayout.js'
import { PlayColumns } from './PlayColumns.js'
import { RaceTrack } from './RaceTrack.js'
import { LANE, RACE_ART, racerPicture } from './raceArt.js'
import { digitFrom, useGameKeys } from './useGameKeys.js'
import { useLeaveOnEscape } from './useLeaveOnEscape.js'
import { useRound, type DealtRound } from './useRound.js'
import { useAnswerCues, useRoundEndCues, useRoundMusic } from './gameSound.js'
import { useRoundFocus } from './useRoundFocus.js'
import { useTrolley } from './useTrolley.js'
import { TRACK_FOR_GAME } from '../sound/cues.js'

/**
 * What to say after an answer.
 *
 * The same three goes as Monkey's, and the same voice: a wrong answer with
 * goes left is an invitation, not a verdict.
 */
function feedbackFor(view: TrolleyView): string {
  const answer = view.lastAnswer
  if (!answer) return ''

  if (answer.correct) return 'Yes!'
  if (answer.moreGoes) return 'Not quite — try again.'

  return `The answer was ${asAnswer(answer.expected, view.answerKind)}. We'll come back to it.`
}

/** An answer as she would say it: money as money, a count as a number. */
function asAnswer(value: number, kind: TrolleyView['answerKind']): string {
  return kind === 'count' ? String(value) : money(value)
}

export function Trolley() {
  // The bar carries the name while she is here, in place of "Hazel's
  // Games". One call rather than one per screen: the chooser and the
  // board are both this component, so the name does not blink out between
  // picking a size and being dealt one.
  useBarTitle(GAMES.trolley.title)

  /*
    The screen's music, not the round's.

    On the round it started when play did — so Sky's was silent through "How
    big a board?", and every game had a beat of nothing while its round was
    being dealt. Asking here means it also survives another go, which is the
    same screen and should not restart the track.
  */
  useRoundMusic(TRACK_FOR_GAME.trolley)

  const { round, again, failed } = useRound<{ levels: SkillLevels }>({ game: 'trolley' })

  if (failed) {
    return (
      <p role="alert" className="text-coral py-16 text-center">
        We couldn’t open the shop just now. Have another go in a moment.
      </p>
    )
  }

  if (!round) {
    return (
      <p role="status" className="py-16 text-center">
        Filling the shelves…
      </p>
    )
  }

  return <TrolleyRound key={round.seed} round={round} onAgain={again} />
}

function TrolleyRound({
  round,
  onAgain,
}: {
  round: DealtRound<{ levels: SkillLevels }>
  onAgain: () => void
}) {
  const leaveOnEscape = useLeaveOnEscape()
  const game = useTrolley(round, onAgain)
  const { view } = game
  const board = useRoundFocus<HTMLDivElement>(round.seed, view.status !== 'playing')

  useAnswerCues(view)
  useRoundEndCues(view.status !== 'playing', game.milestone)

  const [typed, setTyped] = useState('')

  function submit(): void {
    if (typed === '') return
    game.answer(Number.parseInt(typed, 10))
    setTyped('')
  }

  useGameKeys((event) => {
    if (event.key === 'Escape' && view.status !== 'playing') {
      return void leaveOnEscape(false)
    }

    if (view.status !== 'playing') {
      if (event.key === 'Enter') {
        setTyped('')
        game.again()
      }
      return
    }

    const digit = digitFrom(event)
    if (digit) {
      setTyped((current) => (current.length >= MAX_DIGITS ? current : current + digit))
      return
    }

    if (event.key === 'Enter') return submit()
    if (event.key === 'Backspace') return setTyped((current) => current.slice(0, -1))
    if (event.key === 'Escape') {
      if (leaveOnEscape(typed !== '')) return
      return setTyped('')
    }
  })

  return (
    <div className="mx-auto flex w-full max-w-xl [[data-scale=tv]_&]:max-w-none flex-col items-center gap-4">
      <Card
        ref={board}
        tabIndex={-1}
        className="outline-none flex w-full flex-col items-center gap-4"
      >
        <p className="text-ink-muted text-(length:--text-small)">
          {`${view.correct} of ${view.roundLength} done`}
        </p>

        <Announce>
          {[
            view.status !== 'playing'
              ? `${view.correct} out of ${view.roundLength} right.`
              : feedbackFor(view),
            view.status === 'playing' ? `Question: ${view.prompt ?? ''}` : '',
            game.milestone ? `That’s ${game.milestone} games played.` : '',
          ]
            .filter(Boolean)
            .join(' ')}
        </Announce>

        {/*
          Turtley, walking to a full trolley, with Daddy racing the mistakes on
          the same terms as Monkey's race: three wrong answers cost what one
          right answer gains.
        */}
        <RaceTrack
          goal={RACE_ART.trolley}
          racers={[
            {
              key: 'turtley',
              name: 'Turtley',
              picture: racerPicture('turtley'),
              steps: view.correct,
              of: view.roundLength,
              tint: LANE.turtley,
            },
            {
              key: 'daddy',
              name: 'Daddy',
              picture: RACE_ART.daddy,
              steps: view.wrongAnswers,
              of: view.roundLength * TRIES_PER_QUESTION,
              tint: LANE.daddy,
            },
          ]}
        />

        {view.status === 'playing' ? (
          <PlayColumns
            reading={
              <>
                {/* The shop stays on screen while she works. Holding four
                    prices in your head is a memory test, and this is not one. */}
                <ul
                  aria-label="What things cost"
                  // Twice the size on the television. It is a list she holds in
                  // view while working out a sum, and it was the smallest thing
                  // on the screen.
                  className="border-border flex w-full list-none flex-wrap justify-center gap-x-5 gap-y-1 rounded-(--radius-control) border-2 p-3 [[data-scale=tv]_&]:gap-x-10 [[data-scale=tv]_&]:p-6"
                >
                  {view.prices.map((item) => (
                    <li
                      key={item.key}
                      className="text-(length:--text-small) [[data-scale=tv]_&]:text-(length:--text-heading)"
                    >
                      <span className="capitalize">{item.one}</span>{' '}
                      <span className="font-display">{money(item.cents)}</span>
                    </li>
                  ))}
                </ul>

                <div className="flex flex-col items-center gap-3">
                  {view.lastAnswer ? (
                    <p
                      className={`[[data-scale=tv]_&]:text-(length:--text-heading) ${view.lastAnswer.correct ? 'text-teal' : 'text-ink-muted'}`}
                    >
                      {feedbackFor(view)}
                    </p>
                  ) : null}

                  {/*
                    A notch smaller once there is enough of it, and only on the
                    television. See `trolleyLayout` — the longest questions over
                    the fullest shop pushed the frame 20px past the screen, and
                    shrinking every question to suit them would have made the
                    ordinary one worse to read for the sake of the rare one.
                  */}
                  <p
                    className={questionClassName({
                      prompt: view.prompt ?? '',
                      items: view.prices.length,
                    })}
                    aria-label={`Question: ${view.prompt ?? ''}`}
                  >
                    {view.prompt}
                  </p>
                </div>

                {/*
                  The box under the sentence rather than beside it: it is the
                  answer to what she has just read and belongs with it. That
                  leaves the pad alone on the right, which is as much as ten
                  keys need — half a 16:9 screen is a wall of buttons.

                  A non-breaking space when empty, not a plain one: a plain
                  space at the end of an inline box is collapsed away, so the
                  box would stand shorter with nothing in it.
                */}
                <p
                  className="font-display border-border min-h-14 min-w-32 rounded-(--radius-control) border-2 px-4 text-(length:--text-title) tabular-nums"
                  aria-label={
                    typed === ''
                      ? 'No answer yet'
                      : `Your answer: ${asAnswer(Number.parseInt(typed, 10), view.answerKind)}`
                  }
                >
                  {typed === '' ? '\u00a0' : asAnswer(Number.parseInt(typed, 10), view.answerKind)}
                </p>
              </>
            }
            /* In cents, so nothing needs a decimal point — €1.20 is typed as
               120, and the box above shows it back as money. */
            answering={<NumberPad value={typed} onChange={setTyped} onSubmit={submit} />}
            split="mostly-reading"
          />
        ) : (
          <div className="flex flex-col items-center gap-3">
            <p className="font-display text-(length:--text-title)">
              {view.correct === view.roundLength ? 'Shopping done!' : 'Good effort!'}
            </p>
            <p className="text-ink-muted [[data-scale=tv]_&]:text-(length:--text-heading)">{`${view.correct} out of ${view.roundLength} right.`}</p>

            {game.milestone ? <Milestone games={game.milestone} /> : null}

            <PlayAgain
              label="Another go"
              does="to play again"
              onAgain={() => {
                setTyped('')
                game.again()
              }}
            />
          </div>
        )}
      </Card>

      <LeaveGame />

      <KeyLegend
        shortcuts={
          view.status === 'playing'
            ? [
                { keys: ['0', '–', '9'], does: 'Type the answer, in cents' },
                { keys: ['Enter'], does: 'Send it' },
                { keys: ['Bksp'], does: 'Fix a digit' },
                LEAVE_SHORTCUT,
              ]
            : [{ keys: ['Enter'], does: 'Another go' }, LEAVE_SHORTCUT]
        }
      />
    </div>
  )
}
